import { ddRum, rumErrorCtx, SEVERITY } from '@web-platform/browser-rum';
import { saveAs } from 'file-saver';
import I18n from 'i18n-js';
import moment from 'moment';
import _get from 'lodash/get';
import { PARCEL_CATEGORY } from 'constants/country-constants';
import {
  EXCEL_TABLE_VALUE,
  HIDDEN_DATA_COLUMN,
  V2_EXCEL_TABLE_CONTENT_COLS,
} from 'constants/excel-constants';
import { FIELD_PLACEHOLDER } from 'constants/booking-constants';
import { BULK_UPLOAD_LIMIT } from 'constants/bulk-upload-constants';
import { isEmpty } from 'common/utils/data-utils';
import {
  getCreditCardPaymentMethods,
  getGrabPayPaymentMethod,
  getPostpaidPaymentMethod,
} from 'helper/payment';
import { getDefaultErrorContext } from 'common/utils/rum-utils';

function eachColumnInRange(ws, col1, col2, cb) {
  for (let c = col1; c <= col2; c += 1) {
    const col = ws.getColumn(c);
    cb(col);
  }
}

const DROPDOWN_DATA_VALIDATION_COLUMN = {
  SERVICE_TYPE: 'AZ',
  PARCEL_CATEGORY: 'AX',
  VEHICLE_TYPE: 'AW',
};

/* Some payment methods are only allowed for some service types.
  *  On sheet 2, a list of each payment method for each service type is present
  *  The below formula sets the dropdown options available for each row, when a
  *  service type has been selected.
  *  e.g. for row2 (the first delivery), the below formula will reduce to Sheet2!B1:B4
  *  See `generateServiceForSheet2` docstring for more data.
  *  */
const PAYMENT_METHODS_AVAILABLE_FORMULA = row => [
  `INDIRECT(CONCATENATE("Sheet2!",CONCATENATE(Sheet2!C${row +
    10},1),":",CONCATENATE(Sheet2!C${row + 10},Sheet2!E${row + 10})))`,
];

const PAYMENT_METHODS_UNAVAILABLE_FORMULA = row => [
  `INDIRECT(CONCATENATE("Sheet2!",CONCATENATE(Sheet2!C${row +
    10},2),":",CONCATENATE(Sheet2!C${row + 10},Sheet2!E${row + 10})))`,
];

const VEHICLE_TYPE = ['bike', 'car'];

const adjustColumns = ws => {
  eachColumnInRange(ws, 1, ws.columnCount, column => {
    if (column.letter === 'A' || column.letter === 'B') {
      column.width = 15; // eslint-disable-line no-param-reassign
    } else if (column.letter === 'B' || column.letter === 'F') {
      column.width = 20.5; // eslint-disable-line no-param-reassign
    } else if (
      column.letter === 'C' ||
      column.letter === 'G' ||
      column.letter === 'S'
    ) {
      column.width = 25; // eslint-disable-line no-param-reassign
    } else if (
      column.letter === 'D' ||
      column.letter === 'E' ||
      column.letter === 'H' ||
      column.letter === 'I'
    ) {
      column.width = 35; // eslint-disable-line no-param-reassign
    } else if (
      column.letter === 'K' ||
      column.letter === 'L' ||
      column.letter === 'M' ||
      column.letter === 'O' ||
      column.letter === 'P' ||
      column.letter === 'Q' ||
      column.letter === 'R' ||
      column.letter === 'T' ||
      column.letter === 'U' ||
      column.letter === 'N'
    ) {
      column.width = 20; // eslint-disable-line no-param-reassign
    } else if (column.letter === 'J') {
      column.width = 30; // eslint-disable-line no-param-reassign
    }
  });
};

const handleFreezeView = sheet => {
  sheet.views = [
    {
      state: 'frozen',
      xSplit: 2,
      ySplit: 1,
      activeCell: 'B1',
    },
  ];
};

const generateInstructionInfo = sheet => {
  const instructionInfo = {
    A: { value: '#' },
    B: { value: 'senderName' },
    C: { value: 'senderMobileNumber' },
    D: { value: 'senderAddress' },
    E: { value: 'senderLocationDetails' },
    F: { value: 'recipientName' },
    G: { value: 'recipientMobileNumber' },
    H: { value: 'recipientAddress' },
    I: { value: 'receiverLocationDetails' },
    J: { value: 'serviceType' },
    K: { value: 'codItem' },
    L: { value: 'paymentMethods' },
    M: { value: 'promo' },
    N: { value: 'weight' },
    O: { value: 'parcelCategory' },
    P: { value: 'vehicleType' },
    Q: { value: 'batchReference' },
    R: { value: 'sequenceNumber' },
    S: { value: 'deliveryInstruction' },
    T: { value: 'itemNote' },
    U: { value: 'referenceCode' },
  };
  const instructionRow = 1;
  const optionalColumns = ['E', 'I', 'K', 'M', 'Q', 'R', 'S', 'T', 'U'];

  Object.keys(instructionInfo).forEach(col => {
    const cell = sheet.getCell(`${col}${instructionRow}`);
    const staticInfo = instructionInfo[col];
    cell.value =
      col === 'A'
        ? staticInfo.value
        : I18n.t(EXCEL_TABLE_VALUE[staticInfo.value]);
    cell.border =
      col === 'A'
        ? {
            right: { style: 'thin', color: { argb: '00010101' } },
            bottom: { style: 'thin', color: { argb: '00010101' } },
          }
        : {
            right: { style: 'thin', color: { argb: '00010101' } },
            bottom: { style: 'thin', color: { argb: '00010101' } },
            left: { style: 'thin', color: { argb: '00010101' } },
          };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: optionalColumns.includes(col)
        ? { argb: '00ccedbe' }
        : { argb: '00f4c242' },
    };
    cell.font = {
      color: { argb: '00000000' },
      size: 14,
      bold: true,
    };
    cell.alignment = {
      wrapText: true,
    };
  });
};

const generateTableCellContent = (sheet, row, sum) => {
  V2_EXCEL_TABLE_CONTENT_COLS.forEach(col => {
    const cell = sheet.getCell(`${col}${row}`);
    if (col === 'A') {
      cell.value = sum;
    }
    cell.numFmt = '@';
    cell.font = {
      size: 14,
    };
    cell.border = {
      top: { style: 'thin', color: { argb: '00010101' } },
      bottom: { style: 'thin', color: { argb: '00010101' } },
      right: { style: 'thin', color: { argb: '00010101' } },
    };
    cell.alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true,
    };
  });
};

const generateTable = sheet => {
  let sum = 1;
  for (let row = 2; row <= BULK_UPLOAD_LIMIT; row += 1) {
    generateTableCellContent(sheet, row, sum);
    sum += 1;
  }
};

const generateDropdownServiceType = (sheet, serviceTypes, sheet2) => {
  const col = 'J';
  const dataCol = DROPDOWN_DATA_VALIDATION_COLUMN.SERVICE_TYPE;
  for (let row = 2; row <= BULK_UPLOAD_LIMIT; row += 1) {
    const cell = sheet.getCell(`${col}${row}`);
    cell.dataValidation = {
      type: 'list',
      formulae: [`$${dataCol}$1:$${dataCol}$${serviceTypes.length}`], // populate list of service type
    };
    cell.value = serviceTypes[0].name;
    cell.font = {
      size: 14,
    };
    sheet.getCell(`${col}${row}`).alignment = {
      vertical: 'top',
      horizontal: 'left',
    };
    sheet2.getCell(`C${row + 10}`).value = {
      formula: `VLOOKUP(Sheet1!J${row},Sheet2!A10:B19,2,0)`,
    };
    sheet2.getCell(`E${row + 10}`).value = {
      formula: `VLOOKUP(Sheet1!J${row},Sheet2!A10:D19,4,0)`,
    };
  }
  const listServiceTypes = serviceTypes.reduce((acc, element, index) => {
    if (index + 1 === serviceTypes.length) {
      acc += element.ID; // eslint-disable-line no-param-reassign
      return acc;
    }
    acc += element.ID + ','; // eslint-disable-line no-param-reassign
    return acc;
  }, '');
  sheet.getCell(`${dataCol}150`).value = listServiceTypes;
  sheet.getCell(`${dataCol}150`).numFmt = ';;;';
};

const generateDropdownParcelCategory = (sheet, categories) => {
  const col = 'O';
  const dataCol = DROPDOWN_DATA_VALIDATION_COLUMN.PARCEL_CATEGORY;
  for (let row = 2; row <= BULK_UPLOAD_LIMIT; row += 1) {
    const cell = sheet.getCell(`${col}${row}`);
    cell.dataValidation = {
      type: 'list',
      formulae: [`$${dataCol}$1:$${dataCol}$${categories.length}`], // populate list of parcel category
    };
    cell.value = isEmpty(sheet.getCell(`${col}${row}`).value)
      ? I18n.t(`v2.homepage.manualOrder.category.${categories[0]}`)
      : sheet.getCell(`${col}${row}`).value;
    cell.font = {
      size: 14,
    };
    sheet.getCell(`${col}${row}`).alignment = {
      vertical: 'top',
      horizontal: 'left',
    };
  }

  const parcelCategories = categories.reduce((acc, element, index) => {
    if (index + 1 === categories.length) {
      acc += element; // eslint-disable-line no-param-reassign
      return acc;
    }
    acc += element + ','; // eslint-disable-line no-param-reassign
    return acc;
  }, '');
  sheet.getCell(`${dataCol}150`).value = parcelCategories;
  sheet.getCell(`${dataCol}150`).numFmt = ';;;';
};

const generateDropdownVehicleType = (sheet, types) => {
  const col = 'P';
  const dataCol = DROPDOWN_DATA_VALIDATION_COLUMN.VEHICLE_TYPE;
  for (let row = 2; row <= BULK_UPLOAD_LIMIT; row += 1) {
    const cell = sheet.getCell(`${col}${row}`);
    cell.dataValidation = {
      type: 'list',
      formulae: [`$${dataCol}$1:$${dataCol}$${types.length}`], // populate list of parcel size
    };
    cell.value = isEmpty(sheet.getCell(`${col}${row}`).value)
      ? I18n.t(`booking.fieldLabel.options.vehicleType.${types[0]}`)
      : sheet.getCell(`${col}${row}`).value;
    cell.font = {
      size: 14,
    };
    sheet.getCell(`${col}${row}`).alignment = {
      vertical: 'top',
      horizontal: 'left',
    };
  }

  const parcelTypes = types.reduce((acc, element, index) => {
    if (index + 1 === types.length) {
      acc += element; // eslint-disable-line no-param-reassign
      return acc;
    }
    acc += element + ','; // eslint-disable-line no-param-reassign
    return acc;
  }, '');
  sheet.getCell(`${dataCol}150`).value = parcelTypes;
  sheet.getCell(`${dataCol}150`).numFmt = ';;;';
};

const generateDropdownPaymentMethod = (sheet, paymentMethod) => {
  for (let row = 2; row <= BULK_UPLOAD_LIMIT; row += 1) {
    const rowIndex = 'L';
    const cell = sheet.getCell(`${rowIndex}${row}`);
    if (!paymentMethod) {
      cell.dataValidation = {
        type: 'list',
        formulae: PAYMENT_METHODS_UNAVAILABLE_FORMULA(row),
      };
    } else {
      cell.dataValidation = {
        type: 'list',
        formulae: PAYMENT_METHODS_AVAILABLE_FORMULA(row),
      };
    }
    cell.value = paymentMethod;
    cell.font = { size: 14 };
    cell.alignment = { vertical: 'top', horizontal: 'left' };
  }
};

export const generateServiceTypesWithValidPaymentMethods = (
  serviceTypes = [],
  paymentMethods,
  cashlessPaymentEnabled,
  cashPaymentEnabled,
) => {
  // use an array as we want to maintain order, corpBilling comes first if available
  const result = [];
  const enabledPaymentMethods = paymentMethods.filter(p => p.enabled);
  const enabledCorpBilling = getPostpaidPaymentMethod(enabledPaymentMethods);
  const enabledGrabPay = getGrabPayPaymentMethod(enabledPaymentMethods);
  const enabledCreditCards = getCreditCardPaymentMethods(enabledPaymentMethods);
  serviceTypes.forEach(st => {
    const stPaymentMethods = [];
    if (enabledGrabPay) {
      stPaymentMethods.push({
        ...enabledGrabPay,
        verbose: I18n.t(FIELD_PLACEHOLDER.GRAB_PAY),
      });
    }
    if (cashlessPaymentEnabled) {
      if (enabledCorpBilling) {
        stPaymentMethods.push({
          ...enabledCorpBilling,
          verbose: I18n.t(FIELD_PLACEHOLDER.CORP_BILLING),
        });
      }
      enabledCreditCards.forEach(pm => {
        stPaymentMethods.push({
          ...pm,
          verbose: `${pm.type} ${pm.refNumber}`,
        });
      });
    }
    if (cashPaymentEnabled && st.allowCash) {
      stPaymentMethods.push({
        type: I18n.t(FIELD_PLACEHOLDER.CASH_BY_SENDER),
        verbose: I18n.t(FIELD_PLACEHOLDER.CASH_BY_SENDER),
      });
      stPaymentMethods.push({
        type: I18n.t(FIELD_PLACEHOLDER.CASH_BY_RECIPIENT),
        verbose: I18n.t(FIELD_PLACEHOLDER.CASH_BY_RECIPIENT),
      });
    }
    result.push({ ...st, paymentMethods: stPaymentMethods });
  });

  return result;
};

const generateServiceTypesInitial = (sheet, serviceTypes) => {
  const col = DROPDOWN_DATA_VALIDATION_COLUMN.SERVICE_TYPE;
  for (let row = 0; row < serviceTypes.length; row += 1) {
    const serviceInfo = serviceTypes[row];
    const cell = sheet.getCell(`${col}${row + 1}`);
    cell.value = serviceInfo.name;
    cell.font = {
      size: 14,
      color: { argb: 'FFFFFFFF' },
    };

    sheet.getCell(`${col}${row}`).alignment = {
      vertical: 'top',
      horizontal: 'left',
    };
  }
};

const generateParcelCategoryInitial = (sheet, categories) => {
  const col = DROPDOWN_DATA_VALIDATION_COLUMN.PARCEL_CATEGORY;
  for (let row = 0; row < categories.length; row += 1) {
    const catetory = categories[row];
    const cell = sheet.getCell(`${col}${row + 1}`);
    const translation = `v2.homepage.manualOrder.category.${catetory}`;
    cell.value = I18n.t(translation);
    cell.font = {
      size: 14,
      color: { argb: 'FFFFFFFF' },
    };

    sheet.getCell(`${col}${row}`).alignment = {
      vertical: 'top',
      horizontal: 'left',
    };
  }
};

const generateVehicleTypeInitial = (sheet, types) => {
  const col = DROPDOWN_DATA_VALIDATION_COLUMN.VEHICLE_TYPE;
  for (let row = 0; row < types.length; row += 1) {
    const type = types[row];
    const cell = sheet.getCell(`${col}${row + 1}`);
    const translation = `booking.fieldLabel.options.vehicleType.${type}`;
    cell.value = I18n.t(translation);
    cell.font = {
      size: 14,
      color: { argb: 'FFFFFFFF' },
    };

    sheet.getCell(`${col}${row}`).alignment = {
      vertical: 'top',
      horizontal: 'left',
    };
  }
};

const generateSheet1 = (sheet, serviceTypes, sheet2, countryCode) => {
  generateInstructionInfo(sheet);
  handleFreezeView(sheet);
  adjustColumns(sheet);
  generateTable(sheet);
  generateServiceTypesInitial(sheet, serviceTypes);
  generateDropdownServiceType(sheet, serviceTypes, sheet2);

  const firstValidPaymentMethod = _get(
    serviceTypes,
    '[0].paymentMethods[0].verbose',
    I18n.t(FIELD_PLACEHOLDER.CASH_BY_SENDER),
  );
  generateDropdownPaymentMethod(sheet, firstValidPaymentMethod);

  // get parcel categories based on country config
  generateParcelCategoryInitial(sheet, PARCEL_CATEGORY[countryCode]);
  generateDropdownParcelCategory(sheet, PARCEL_CATEGORY[countryCode]);
  generateVehicleTypeInitial(sheet, VEHICLE_TYPE);
  generateDropdownVehicleType(sheet, VEHICLE_TYPE);
};

const writeCellValues = (sheet, values) => {
  Object.keys(values).forEach(key => {
    const cellData = values[key];
    const cell = sheet.getCell(key);
    Object.assign(cell, cellData);
  });
};

const setFontSizeForCellValues = (cellValues, fontSize) => {
  Object.keys(cellValues).forEach(key => {
    const cellValue = cellValues[key];
    Object.assign(cellValue, { font: { size: fontSize } });
  });
};

export const generateServiceForSheet2 = serviceTypes => {
  /** Generates 2 blocks of data
   *  e.g. with 5 service types
   *  First block:
   *  Rows 1:5
   *  Column A: List of all service types names
   *  Column B-F: List of payment methods valid for each service types
   *  Column B corresponds to first service type, Column C to second service type etc.
   *
   * Block 2:
   * Rows 10-14
   * Column A: List of all service type names
   * Column B: Letters referring to the column that contains the payment methods for this service type
   * Column E: Number of valid payment methods for this service type
   *
   * Explanation:
   * The above is for populating the payment methods dropdown.
   * Some payment methods are only valid for some service types. When a service type is selected, the payment methods
   * dropdown needs to change to reflect the correct payment methods.
   * When a service type is selected in sheet1, a lookup is done in Block 2 data above.
   * When a service type is matched, the letter in Column B is used to select which column contains the payment methods
   * for this service type. Column E numbers are used to decide how many options should be in the dropdown.
   */
  const cellValues = {};
  for (let row = 0; row < serviceTypes.length; row += 1) {
    const serviceInfo = serviceTypes[row];
    cellValues[`A${row + 1}`] = {
      value: serviceInfo.name,
    };
    const lengthPaymentMethods = serviceInfo.paymentMethods.length;
    const cellForLookUp = `A${row + 10}`;
    cellValues[cellForLookUp] = {
      value: serviceInfo.name,
    };

    const cellDefinedForDropdown = `B${row + 10}`;
    cellValues[cellDefinedForDropdown] = {
      value: `${String.fromCharCode(row + 66)}`,
    };

    const cellDefinedForPayment = `D${row + 10}`;
    cellValues[cellDefinedForPayment] = { value: lengthPaymentMethods };

    serviceInfo.paymentMethods.forEach((pm, j) => {
      cellValues[`${String.fromCharCode(row + 66)}${j + 1}`] = {
        value: pm.verbose,
      };
    });
  }
  return cellValues;
};

const generateSheet2 = (sheet, serviceTypes) => {
  const cellValues = generateServiceForSheet2(serviceTypes);
  setFontSizeForCellValues(cellValues, 14);
  writeCellValues(sheet, cellValues);
};

const generateExcel = async ({
  cityID,
  serviceTypes,
  paymentMethods,
  countryCode,
  cityName,
  cityCode,
  isCashlessPaymentDisabled,
  isCashPaymentDisabled,
  callback = null,
}) => {
  try {
    const ExcelModule = await import('exceljs/dist/exceljs');
    const Excel = ExcelModule.default;

    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    sheet.getCell(HIDDEN_DATA_COLUMN.CITY_ID).value = cityID;
    sheet.getCell(HIDDEN_DATA_COLUMN.CITY_ID).numFmt = ';;;';
    const sheetPaymentAndService = workbook.addWorksheet('Sheet2');
    sheetPaymentAndService.state = 'hidden';

    const pm = _get(paymentMethods, 'result', []);
    const cashlessPaymentEnabled = !isCashlessPaymentDisabled;
    const cashPaymentEnabled = !isCashPaymentDisabled;
    const serviceTypesWithValidPaymentMethods = generateServiceTypesWithValidPaymentMethods(
      serviceTypes,
      pm,
      cashlessPaymentEnabled,
      cashPaymentEnabled,
    );

    generateSheet1(
      sheet,
      serviceTypesWithValidPaymentMethods,
      sheetPaymentAndService,
      countryCode,
    );
    generateSheet2(sheetPaymentAndService, serviceTypesWithValidPaymentMethods);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(
      blob,
      `GRABEXPRESS-${cityCode}-${moment().format('DD/MM/YYYY')}.xlsx`,
    );

    if (callback) callback();
  } catch (error) {
    const errorCtx = rumErrorCtx(getDefaultErrorContext())
      .component('generateExcel')
      .message(error.message || 'Failed to generate excel file')
      .severity(SEVERITY.MEDIUM)
      .build();
    ddRum.addError(error, errorCtx);
  }
};

export default generateExcel;
